import React, { useState, useEffect, useMemo } from 'react';
import { clientReportsService } from '../services';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  FileText, 
  Download, 
  Building2, 
  Calendar, 
  AlertCircle, 
  CheckCircle2,
  ChevronRight,
  Loader2,
  Search,
  FolderOpen
} from 'lucide-react';

/**
 * Client Reports Page - Phase 2.3A
 * Allows client users to browse and download published reports for their accessible sites.
 * Flow: Site selector → Completed jobs list → Published reports list → Download
 */
const ClientReportsPage = () => {
  const { isAnonymised, anonymise } = useImpersonation();
  // State
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [reports, setReports] = useState([]);
  const [summaryReports, setSummaryReports] = useState([]);
  
  // Loading states
  const [loadingSites, setLoadingSites] = useState(true);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadingSummaryId, setDownloadingSummaryId] = useState(null);
  
  // Error states
  const [sitesError, setSitesError] = useState(null);
  const [jobsError, setJobsError] = useState(null);
  const [reportsError, setReportsError] = useState(null);
  const [downloadError, setDownloadError] = useState(null);

  // Search filter for site selector
  const [siteSearch, setSiteSearch] = useState('');

  // Load sites on mount
  useEffect(() => {
    const fetchSites = async () => {
      setLoadingSites(true);
      setSitesError(null);
      try {
        const data = await clientReportsService.listSites();
        setSites(data);
        // Auto-select if only one site
        if (data.length === 1) {
          setSelectedSiteId(data[0].site_id);
        }
      } catch (error) {
        if (error.response?.status === 403 || error.response?.status === 401) {
          setSitesError("You don't have access to view sites.");
        } else {
          setSitesError("Something went wrong. Please try again.");
        }
      } finally {
        setLoadingSites(false);
      }
    };
    fetchSites();
  }, []);

  // Load jobs when site changes
  useEffect(() => {
    if (!selectedSiteId) {
      setJobs([]);
      setSelectedJobId(null);
      setReports([]);
      setSummaryReports([]);
      return;
    }

    const fetchJobs = async () => {
      setLoadingJobs(true);
      setJobsError(null);
      setSelectedJobId(null);
      setReports([]);
      setSummaryReports([]);
      try {
        const data = await clientReportsService.listSiteJobs(selectedSiteId, { limit: 50 });
        setJobs(data);
      } catch (error) {
        if (error.response?.status === 403) {
          setJobsError("You don't have access to this site.");
        } else {
          setJobsError("Something went wrong. Please try again.");
        }
        setJobs([]);
      } finally {
        setLoadingJobs(false);
      }
    };
    fetchJobs();
  }, [selectedSiteId]);

  // Load reports when job changes
  useEffect(() => {
    if (!selectedJobId) {
      setReports([]);
      setSummaryReports([]);
      return;
    }

    const fetchReports = async () => {
      setLoadingReports(true);
      setReportsError(null);
      try {
        const [stockReports, summaryData] = await Promise.all([
          clientReportsService.listJobReports(selectedJobId),
          clientReportsService.listJobSummaryReports(selectedJobId).catch(() => [])
        ]);
        setReports(stockReports);
        setSummaryReports(summaryData);
      } catch (error) {
        if (error.response?.status === 403) {
          setReportsError("You don't have access to this item.");
        } else {
          setReportsError("Something went wrong. Please try again.");
        }
        setReports([]);
        setSummaryReports([]);
      } finally {
        setLoadingReports(false);
      }
    };
    fetchReports();
  }, [selectedJobId]);

  // Handle download
  const handleDownload = async (report) => {
    if (!report.latest_version) return;
    
    setDownloadingId(report.report_id);
    setDownloadError(null);
    try {
      await clientReportsService.downloadReport(
        report.latest_version.version_id,
        report.latest_version.filename
      );
    } catch (error) {
      setDownloadError(error.message);
    } finally {
      setDownloadingId(null);
    }
  };

  // Handle summary report PDF download
  const handleDownloadSummary = async (summaryReport) => {
    setDownloadingSummaryId(summaryReport.id);
    setDownloadError(null);
    try {
      const scopeLabel = summaryReport.scope === 'FOOD' ? 'Food' : 'Beverage';
      const filename = `Audit_Summary_${scopeLabel}_${summaryReport.audit_date}.pdf`;
      await clientReportsService.downloadSummaryReportPdf(summaryReport.id, filename);
    } catch (error) {
      setDownloadError(error.message);
    } finally {
      setDownloadingSummaryId(null);
    }
  };

  // Filter sites by search
  const filteredSites = useMemo(() => {
    if (!siteSearch) return sites;
    const search = siteSearch.toLowerCase();
    return sites.filter(s => {
      const displayName = isAnonymised ? anonymise(s.site_name, 'site') : s.site_name;
      const displayGroup = s.group_name ? (isAnonymised ? anonymise(s.group_name, 'group') : s.group_name) : '';
      return displayName.toLowerCase().includes(search) || displayGroup.toLowerCase().includes(search);
    });
  }, [sites, siteSearch, isAnonymised, anonymise]);

  // Group reports by service type for display
  const groupedReports = useMemo(() => {
    const groups = {};
    reports.forEach(report => {
      const key = report.service_type;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(report);
    });
    return groups;
  }, [reports]);

  // Get selected site name
  const selectedSite = sites.find(s => s.site_id === selectedSiteId);
  const selectedJob = jobs.find(j => j.job_id === selectedJobId);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Format report type for display
  const formatReportType = (type) => {
    const labels = {
      'STOCK': 'Stock Report',
      'AUDIT': 'Audit Report',
      'DASHBOARD': 'Dashboard'
    };
    return labels[type] || type;
  };

  // Format service type for display
  const formatServiceType = (type) => {
    const labels = {
      'FOOD': 'Food',
      'BEVERAGE': 'Beverage'
    };
    return labels[type] || type;
  };

  // Render no sites assigned state
  if (!loadingSites && sites.length === 0 && !sitesError) {
    return (
      <div className="space-y-6" data-testid="client-reports-page">
        <h1 className="text-2xl font-bold text-slate-900">Reports</h1>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-amber-900">No Sites Assigned</h3>
                <p className="text-sm text-amber-700 mt-1">
                  No sites are currently assigned to your account. Please contact Abbey 5 Star for assistance.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-reports-page">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Reports</h1>
        <p className="text-sm text-slate-500 mt-1">
          Browse and download published reports for your sites
        </p>
      </div>

      {/* Download Error Alert */}
      {downloadError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <span className="text-sm text-red-700">{downloadError}</span>
          <button 
            onClick={() => setDownloadError(null)}
            className="ml-auto text-red-600 hover:text-red-800"
          >
            ×
          </button>
        </div>
      )}

      {/* Site Selector */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="h-4 w-4 text-emerald-600" />
            Select Site
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingSites ? (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading sites...</span>
            </div>
          ) : sitesError ? (
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-4 w-4" />
              <span>{sitesError}</span>
            </div>
          ) : (
            <Select
              value={selectedSiteId?.toString() || ''}
              onValueChange={(value) => setSelectedSiteId(parseInt(value))}
            >
              <SelectTrigger 
                className="w-full max-w-md"
                data-testid="site-selector"
              >
                <SelectValue placeholder="Choose a site..." />
              </SelectTrigger>
              <SelectContent>
                <div className="p-2 border-b">
                  <div className="flex items-center gap-2 px-2 py-1 bg-slate-50 rounded">
                    <Search className="h-4 w-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search sites..."
                      value={siteSearch}
                      onChange={(e) => setSiteSearch(e.target.value)}
                      className="flex-1 bg-transparent text-sm outline-none"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
                {filteredSites.map(site => (
                  <SelectItem 
                    key={site.site_id} 
                    value={site.site_id.toString()}
                  >
                    <div className="flex flex-col">
                      <span>{anonymise(site.site_name, 'site')}</span>
                      {site.group_name && (
                        <span className="text-xs text-slate-500">{anonymise(site.group_name, 'group')}</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
                {filteredSites.length === 0 && (
                  <div className="p-3 text-sm text-slate-500 text-center">
                    No sites found
                  </div>
                )}
              </SelectContent>
            </Select>
          )}
        </CardContent>
      </Card>

      {/* Main Content - Two Column Layout */}
      {selectedSiteId && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Jobs Column */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-4 w-4 text-emerald-600" />
                Completed Audits
                {selectedSite && (
                  <span className="text-sm font-normal text-slate-500">
                    - {anonymise(selectedSite.site_name, 'site')}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingJobs ? (
                <div className="flex items-center gap-2 text-slate-500 py-8 justify-center">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading audits...</span>
                </div>
              ) : jobsError ? (
                <div className="flex items-center gap-2 text-red-600 py-8 justify-center">
                  <AlertCircle className="h-4 w-4" />
                  <span>{jobsError}</span>
                </div>
              ) : jobs.length === 0 ? (
                <div className="flex flex-col items-center gap-2 text-slate-500 py-8">
                  <FolderOpen className="h-8 w-8 text-slate-300" />
                  <span className="text-sm">No completed audits available for this site yet.</span>
                </div>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {jobs.map(job => (
                    <button
                      key={job.job_id}
                      onClick={() => setSelectedJobId(job.job_id)}
                      data-testid={`job-item-${job.job_id}`}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedJobId === job.job_id
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">
                              {formatDate(job.scheduled_date)}
                            </span>
                            {job.has_published_reports && (
                              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {job.status}
                          </p>
                        </div>
                        <ChevronRight className={`h-4 w-4 ${
                          selectedJobId === job.job_id ? 'text-emerald-600' : 'text-slate-400'
                        }`} />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Reports Column */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4 text-emerald-600" />
                Published Reports
                {selectedJob && (
                  <span className="text-sm font-normal text-slate-500">
                    - {formatDate(selectedJob.scheduled_date)}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedJobId ? (
                <div className="flex flex-col items-center gap-2 text-slate-500 py-8">
                  <FileText className="h-8 w-8 text-slate-300" />
                  <span className="text-sm">Select an audit to view reports</span>
                </div>
              ) : loadingReports ? (
                <div className="flex items-center gap-2 text-slate-500 py-8 justify-center">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading reports...</span>
                </div>
              ) : reportsError ? (
                <div className="flex items-center gap-2 text-red-600 py-8 justify-center">
                  <AlertCircle className="h-4 w-4" />
                  <span>{reportsError}</span>
                </div>
              ) : reports.length === 0 && summaryReports.length === 0 ? (
                <div className="flex flex-col items-center gap-2 text-slate-500 py-8">
                  <FolderOpen className="h-8 w-8 text-slate-300" />
                  <span className="text-sm">No published reports available for this audit.</span>
                </div>
              ) : (
                <div className="space-y-4 max-h-[500px] overflow-y-auto">
                  {/* Stock Audit Reports */}
                  {Object.entries(groupedReports).map(([serviceType, serviceReports]) => (
                    <div key={serviceType}>
                      <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                        {formatServiceType(serviceType)} — Stock Audit Reports
                      </h4>
                      <div className="space-y-2">
                        {serviceReports.map(report => (
                          <div
                            key={report.report_id}
                            data-testid={`report-item-${report.report_id}`}
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-slate-500" />
                                <span className="font-medium text-slate-900">
                                  {formatReportType(report.report_type)}
                                </span>
                              </div>
                              {report.latest_version && (
                                <p className="text-xs text-slate-500 mt-0.5 ml-6">
                                  Uploaded {formatDate(report.latest_version.uploaded_at)}
                                </p>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownload(report)}
                              disabled={!report.latest_version || downloadingId === report.report_id || isAnonymised}
                              title={isAnonymised ? 'Downloads disabled in Anonymised Demo Mode' : undefined}
                              data-testid={`download-btn-${report.report_id}`}
                              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                            >
                              {downloadingId === report.report_id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <Download className="h-4 w-4 mr-1" />
                                  Download
                                </>
                              )}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Audit Summary Reports */}
                  {summaryReports.length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                        Audit Summary Reports
                      </h4>
                      <div className="space-y-2">
                        {summaryReports.map(sr => (
                          <div
                            key={`summary-${sr.id}`}
                            data-testid={`summary-report-item-${sr.id}`}
                            className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-blue-500" />
                                <span className="font-medium text-slate-900">
                                  {sr.scope === 'FOOD' ? 'Food' : 'Beverage'} Audit Summary
                                </span>
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5 ml-6">
                                {sr.stock_number} — Published {formatDate(sr.published_at)}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownloadSummary(sr)}
                              disabled={downloadingSummaryId === sr.id || isAnonymised}
                              title={isAnonymised ? 'Downloads disabled in Anonymised Demo Mode' : undefined}
                              data-testid={`download-summary-btn-${sr.id}`}
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            >
                              {downloadingSummaryId === sr.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <Download className="h-4 w-4 mr-1" />
                                  Download
                                </>
                              )}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="bg-blue-100 rounded-full p-2">
              <AlertCircle className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium text-blue-900 mb-1">
                About Reports
              </h3>
              <p className="text-sm text-blue-700">
                This page shows published audit reports for sites assigned to your account. 
                Only completed audits with published reports are displayed. 
                For any questions about your reports, please contact your account manager.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientReportsPage;
